import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ChevronDown,
  ChevronUp,
  Video,
  Clock,
  FileText,
  CheckCircle2,
  Binary as Binoculars,
  Box,
  Stethoscope,
  Syringe,
  Volume2,
  Baby,
  Building2,
  Ambulance,
  HeartCrack,
  Scale,
  MessageCircleMore,
  HeartHandshake,
  Crown,
  Dumbbell,
  Brain,
  Package,
  Bird,
  Armchair,
  BedDouble,
  Bath,
  RockingChair,
  Heart,
  Milk,
  Utensils,
  Accessibility,
  Activity,
  Ruler,
  Wind,
  Flame,
  Pill,
  UtensilsCrossed,
  Angry,
  Activity as ActivityIcon,
  HandHeart,
  Frown,
  Droplets,
  Bone,
  TestTube2,
  ShieldPlus,
  BellElectric
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useFont } from '../contexts/FontContext';
import { useSubscription } from '../hooks/useSubscription';
import BackButton from '../components/BackButton';
import { api } from '../services/api';
import { getModules, getSubModules, getModuleCourses, getSubModuleProgress } from '../mockApi';
import type { Module, SubModule, ModuleCourse, SubModuleProgress, CourseProgress } from '../types';

const iconMap: Record<string, typeof FileText> = {
  'Binoculars': Binoculars,
  'Box': Box,
  'Stethoscope': Stethoscope,
  'Syringe': Syringe,
  'Bell-Electric': BellElectric,
  'Volume-2': Volume2,
  'Baby': Baby,
  'Hospital': Building2,
  'Ambulance': Ambulance,
  'Heart-Crack': HeartCrack,
  'Scale': Scale,
  'Message-Circle-More': MessageCircleMore,
  'Hand-Heart': HandHeart,
  'Crown': Crown,
  'Biceps-Flexed': Dumbbell,
  'Brain': Brain,
  'Boxes': Package,
  'Bird': Bird,
  'Armchair': Armchair,
  'Bed-Double': BedDouble,
  'Toilet': Bath,
  'Rocking-Chair': RockingChair,
  'Heart-Handshake': HeartHandshake,
  'Milk': Milk,
  'Ustensils': Utensils,
  'Accessibility': Accessibility,
  'Heart-Pulse': Activity,
  'Ruler': Ruler,
  'Wind': Wind,
  'Flame': Flame,
  'Pill': Pill,
  'Ustensils-Crossed': UtensilsCrossed,
  'Angry': Angry,
  'Activity': ActivityIcon,
  'Hand-Helping': HandHeart,
  'Frown': Frown,
  'Droplets': Droplets,
  'Bone': Bone,
  'Test-Tube-Diagonal': TestTube2,
  'Shield-Plus': ShieldPlus
};

export default function ModuleDetail() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { font } = useFont();
  const { hasActiveSubscription } = useSubscription();

  const [moduleId, setModuleId] = useState<string | null>(null);
  const [module, setModule] = useState<Module | null>(null);
  const [subModules, setSubModules] = useState<SubModule[]>([]);
  const [courses, setCourses] = useState<Record<string, ModuleCourse[]>>({});
  const [courseProgress, setCourseProgress] = useState<Record<string, CourseProgress>>({});
  const [progress, setProgress] = useState<Record<string, number>>({});
  const [expandedSubModules, setExpandedSubModules] = useState<Record<string, boolean>>({});

  const slugify = (text: string): string => {
    return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };
  const normaliseCourseProgress = (
  course: ModuleCourse,
  value: number | null
): CourseProgress => ({
  courseId: course.id,
  progress: value === 100 ? 100 : 0,
  completed: value === 100,
  lastAccessedAt: new Date().toISOString()
})

  const getProgress = (subModuleId: string): number => {
    return progress[subModuleId] || 0;
  };

  useEffect(() => {
    if (code) {
      getModules().then(modules => {
        const currentModule = modules.find(m => m.code === code);
        if (currentModule) {
          setModule(currentModule);
          setModuleId(currentModule.id);
        }
      });
    }
  }, [code]);

  useEffect(() => {
    if (moduleId) {
      getSubModules(moduleId).then(subModules => {
        setSubModules(subModules);

        // Fetch progress for all submodules immediately
        Promise.all(
          subModules.map(subModule =>
            api.getSubModuleProgress(subModule.id)
              .then(value => [subModule.id, value])
              .catch(() => [subModule.id, 0])
          )
        ).then(progressResults => {
          const progressMap = Object.fromEntries(progressResults);
          setProgress(progressMap);
        });

        // Load expanded state from localStorage
        const savedState = localStorage.getItem(`expandedSubModules-${code}`);
        if (savedState) {
          const expandedState = JSON.parse(savedState);
          setExpandedSubModules(expandedState);

          // Pre-fetch courses for expanded submodules
          Object.entries(expandedState).forEach(([subModuleId, isExpanded]) => {
            if (isExpanded) {
              getModuleCourses(subModuleId).then(moduleCourses => {
                setCourses(prev => ({ ...prev, [subModuleId]: moduleCourses }));

                // Fetch progress for each course
                const progressPromises = moduleCourses.map(course =>
                  api.getCourseProgress(course.id)
                    .then(value => normaliseCourseProgress(course, value))   // 100 ➜ objet, null ➜ objet
                    .catch(() => normaliseCourseProgress(course, null))      // sécurité réseau
                )

                Promise.all(progressPromises).then(progressResults => {
                  const progressMap = progressResults.reduce((acc, progress) => {
                    acc[progress.courseId] = progress;
                    return acc;
                  }, {} as Record<string, CourseProgress>);

                  setCourseProgress(prev => ({ ...prev, ...progressMap }));
                });
              });
            }
          });
        }
      });
    }
  }, [moduleId]);

  const toggleSubModule = async (subModuleId: string) => {
    const isCurrentlyExpanded = expandedSubModules[subModuleId];
    const newState = { ...expandedSubModules, [subModuleId]: !isCurrentlyExpanded };
    setExpandedSubModules(newState);
    localStorage.setItem(`expandedSubModules-${code}`, JSON.stringify(newState));

    if (!isCurrentlyExpanded && !courses[subModuleId]) {
      const moduleCourses = await getModuleCourses(subModuleId);

      setCourses(prev => ({ ...prev, [subModuleId]: moduleCourses }));

      // Fetch progress for each course
      const progressPromises = moduleCourses.map(course =>
        api.getCourseProgress(course.id)
          .then(value => normaliseCourseProgress(course, value))
          .catch(() => normaliseCourseProgress(course, null))
      );

      const progressResults = await Promise.all(progressPromises);
      const progressMap = progressResults.reduce((acc, progress) => {
        acc[progress.courseId] = progress;
        return acc;
      }, {} as Record<string, CourseProgress>);

      setCourseProgress(prev => ({ ...prev, ...progressMap }));
    }
  };

  if (!module) return null;

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: font.family }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <BackButton />
        </div>
        <div className="mb-8">
          <h1 className={`text-3xl font-bold text-${theme.text} mb-2`}>
            {module.code} - {module.title}
          </h1>
          <div
            className="markdown-content text-gray-600"
            dangerouslySetInnerHTML={{
              __html: module.description
                .split('\n')
                .map(line => line.trim())
                .map(line => {
                  if (line.startsWith('* ')) {
                    return `<li>${line.substring(2)}</li>`;
                  }
                  return line;
                })
                .join('\n')
                .replace(/(<li>.*?<\/li>\n*)+/g, '<ul>$&</ul>')
            }}
          />
        </div>

        <div className="space-y-4">
          {subModules.map((subModule) => (
            <div key={subModule.id} className="bg-white rounded-lg shadow-sm border border-gray-100">
              <button
                onClick={() => toggleSubModule(subModule.id)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className={`p-2 rounded-lg bg-${theme.secondary} mr-4`}>
                  {(() => {
                    const Icon = subModule.icon ? iconMap[subModule.icon] || FileText : FileText;
                    return <Icon className={`h-5 w-5 text-${theme.text}`} />;
                  })()}
                </div>
                <div className="text-left flex-1">
                  <h3 className="font-semibold mb-1">{subModule.title}</h3>
                  {/* Progress bar on mobile, hidden on desktop */}
                  <div className="block sm:hidden">
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full bg-${theme.primary} transition-all duration-300`}
                        style={{ width: `${getProgress(subModule.id)}%` }}
                      />
                    </div>
                  </div>
                </div>
                {/* Progress section - hidden on mobile, visible on desktop */}
                <div className="hidden sm:flex items-center space-x-2 sm:space-x-4 min-w-0">
                  <div className="w-16 sm:w-24 h-3 bg-gray-200 rounded-full overflow-hidden flex-shrink-0">
                    <div
                      className={`h-full bg-${theme.primary} transition-all duration-300`}
                      style={{ width: `${getProgress(subModule.id)}%` }}
                    />
                  </div>
                  <span className="text-xs sm:text-sm font-medium text-gray-600 w-8 sm:w-12 text-right flex-shrink-0">
                    {getProgress(subModule.id)}%
                  </span>
                </div>
                {/* Chevron icon - always visible */}
                <div className="flex items-center ml-2 sm:ml-0">
                  {expandedSubModules[subModule.id] ? (
                    <ChevronUp className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
                  )}
                </div>
              </button>

              {expandedSubModules[subModule.id] && courses[subModule.id] && (
                <div className="border-t border-gray-100">
                  {courses[subModule.id].map((course) => (
                    <div
                      key={course.id}
                      onClick={() => {
                        // Check if course is free or user has subscription
                        if (course.free || hasActiveSubscription()) {
                          // Save expanded state before navigating
                          localStorage.setItem(`expandedSubModules-${code}`, JSON.stringify(expandedSubModules));
                          navigate(`/cours/${code}/${slugify(course.title)}`);
                        } else {
                          // Redirect to pricing page
                          navigate('/tarifs');
                        }
                      }}
                      className={`px-6 py-4 border-b last:border-b-0 border-gray-100 transition-colors relative ${
                        course.free || hasActiveSubscription() 
                          ? 'hover:bg-gray-50 cursor-pointer' 
                          : 'hover:bg-red-50 cursor-pointer opacity-75'
                      }`}
                    >
                      <div className="flex items-center mb-2">
                        <div className="relative">
                          {course.icon ? (
                            (() => {
                              const Icon = iconMap[course.icon] || FileText;
                              return <Icon className={`h-4 w-4 mr-2 text-${theme.text}`} />;
                            })()
                          ) : course.hasVideo ? (
                            <Video className={`h-4 w-4 mr-2 text-${theme.text}`} />
                          ) : (
                            <FileText className={`h-4 w-4 mr-2 text-${theme.text}`} />
                          )}
                        </div>
                        <h4 className="font-medium flex-1">{course.title}</h4>
                        {courseProgress[course.id]?.completed && (
                          <CheckCircle2 className={`h-5 w-5 text-${theme.primary} ml-2`} />
                        )}
                      </div>
                      <p className="text-sm text-gray-600 ml-6">{course.description}</p>

                      {/* Lock overlay for premium courses */}
                      {!course.free && !hasActiveSubscription() && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90">
                          <div className="text-center">
                            <div className="w-8 h-8 mx-auto mb-2 bg-red-100 rounded-full flex items-center justify-center">
                              <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <p className="text-xs text-red-600 font-medium">Contenu premium</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}